import React, { createContext, useState, useContext, useEffect } from 'react';
import { LANGUAGES, DEFAULT_LANGUAGE, getTranslation } from './languageUtils';

// Create a context for language preferences
const LanguageContext = createContext();

// Custom hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Provider component that wraps the app and provides language context
export const LanguageProvider = ({ children }) => {
  // Get saved language from local storage or use default
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language');
    return savedLanguage && Object.values(LANGUAGES).includes(savedLanguage) 
      ? savedLanguage 
      : DEFAULT_LANGUAGE;
  });

  // Function to toggle between languages
  const toggleLanguage = () => {
    const newLanguage = language === LANGUAGES.EN ? LANGUAGES.MY : LANGUAGES.EN;
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };
  
  // Function to directly change to a specific language
  const changeLanguage = (newLanguage) => {
    if (Object.values(LANGUAGES).includes(newLanguage)) {
      setLanguage(newLanguage);
      localStorage.setItem('language', newLanguage);
    }
  };

  // Convenience function to get translations with current language
  const t = (key, replacements = {}) => getTranslation(key, language, replacements);

  // Update html lang attribute when language changes
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Value provided to consumers
  const value = {
    language,
    setLanguage,
    toggleLanguage,
    changeLanguage,
    t,
    isEnglish: language === LANGUAGES.EN,
    isMalay: language === LANGUAGES.MY
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext; 